import { Box } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../../../services/httpClient';
import { VACANCY_ENDPOINTS } from '../../../../services/endpoints/rec/vacancy.endpoints';
import Button from '../../../common/Button';
import ConfirmationModal from '../../../common/ConfirmationModal';
import Loading from '../../../common/Loading';
import VacancyBasicCard from './VacancyBasicCard';
import type {
  BasicEntry,
  CustomerEntry,
  QualificationEntry,
  SkillEntry,
  TechnologyEntry,
  VacancyApiResponse,
} from '../../../../services/types/vacancy.types';
import VacancyCustomerCard from './VacancyCustomerCard';
import VacancySkillCard from './VacancySkillCard';
import { usePermissions } from '../../../../context/PermissionContext';
import { API_HEADERS } from '../../../../constants/apiHeaders';
import { PAGE_IDS, PERMISSION_IDS } from '../../../../constants/permissions';
import { buildAddVacancyPayload, buildEditVacancyFormData } from '../../../../utils/vacancyFormData';
import { validateVacancyForm } from '../../../../utils/vacancyValidation';

interface AddVacancyFormProps {
  type: 'add' | 'edit';
  onCancel: () => void;
  onSuccess: (message?: string) => void;
  onError: (message?: string) => void;
  setAddedVacancyId?: (id: number) => void;
  addedVacancyId?: number | null;
}

const VacancyForm: React.FC<AddVacancyFormProps> = ({
  type,
  onCancel,
  onSuccess,
  onError,
  setAddedVacancyId,
  addedVacancyId,
}) => {
  const params = useParams();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { hasPermission, pageId, setPageId } = usePermissions();
  const [basicData, setBasicData] = useState<BasicEntry>({
    vacancyType: '',
    vacancyTitle: '',
    vacancyCode: '',
    minIndustryExperience: '',
    startDate: '',
    endDate: '',
    dueDate: '',
    vacancyReason: '',
    designation: null,
    isActive: true,
    isVisible:true,
    approvalStatus: 0,
    jobPostPublished: 0,
    jobPostPublishedStatus: false
  });
  const [initialIsActive, setInitialIsActive] = useState<boolean>(true);
  const [initialIsVisible, setInitialIsVisible] = useState<boolean>(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [customers, setCustomers] = useState<CustomerEntry[]>([{
    id: '', name: '', countryCode: '', numberOfVacancies: '', revenuePerResource: '',
    rowId: ''
  }]);
  const [qualifications, setQualifications] = useState<QualificationEntry[]>([{ id: '', qualification: null, type: '' }]);
  const [primarySkills, setPrimarySkills] = useState<SkillEntry[]>([{ id: '', skill: null, years: '' }]);
  const [secondarySkills, setSecondarySkills] = useState<SkillEntry[]>([{ id: '', skill: null, years: '' }]);
  const [primaryTechnologies, setPrimaryTechnologies] = useState<TechnologyEntry[]>([{ id: '', technology: null, years: '' }]);
  const [secondaryTechnologies, setSecondaryTechnologies] = useState<TechnologyEntry[]>([{ id: '', technology: null, years: '' }]);
  
  // Confirmation modal state
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    if (
      (type === 'add' && pageId !== PAGE_IDS.VACANCY_ADD) ||
      (type === 'edit' && pageId !== PAGE_IDS.VACANCY_EDIT)
    ) {
      setPageId(type === 'add' ? PAGE_IDS.VACANCY_ADD : PAGE_IDS.VACANCY_EDIT);
      return;
    }

    const vacancyId = params?.id ? params.id : addedVacancyId;
    if (vacancyId === undefined || vacancyId === null) return;

    setIsLoading(true);
    api
      .get<VacancyApiResponse>(VACANCY_ENDPOINTS.byId(vacancyId), {
        headers: { ...API_HEADERS.VACANCY_FORM_DEFAULT },
      })
      .then((response) => {
        const data = response.data;

      // Map basic data
      setBasicData({
        vacancyType: data.vacancyType || '',
        vacancyTitle: data.vacancyTitle || '',
        vacancyCode: data.vacancyCode || '',
        minIndustryExperience: data.minimumIndustryExperience?.toString() || '',
        startDate: data.vacancyStartDate || '',
        endDate: data.vacancyEndDate || '',
        dueDate: data.vacancyDueDate || '',
        vacancyReason: data.vacancyReason || '',
        designation: data.designation ? { value: data.designation.id.toString(), label: data.designation.designation } : null,
        isActive: data.isActive,
        isVisible: data.isVisible,
        approvalStatus: data.approvalStatus,
        jobPostPublished: data.jobPostPublished ?? 0,
        jobPostPublishedStatus: data.jobPostPublishedStatus
      });
      
      // Store initial isActive value
      setInitialIsActive(data.isActive);
      setInitialIsVisible(data.isVisible);

      // Map customers
      if (data.customers && data.customers.length > 0) {
        setCustomers(data.customers.map((customer) => ({
          id: customer.id,
          name: customer.name || '',
          countryCode: customer.countryCode || '',
          numberOfVacancies: customer.numberOfVacancies?.toString() || '',
          revenuePerResource: customer.revenuePerResource?.toString() || '',
          rowId: crypto.randomUUID()
        })));
      }

      // Map qualifications
      if (data.qualifications && data.qualifications.length > 0) {
        setQualifications(data.qualifications.map((qual, index) => ({
          id: (index + 1).toString(),
          qualification: qual.id ? { value: qual.id.toString(), label: qual.name } : null,
          type: qual.qualificationType || ''
        })));
      }

      // Map primary skills
      if (data.primarySkills && data.primarySkills.length > 0) {
        setPrimarySkills(data.primarySkills.map((skill, index) => ({
          id: (index + 1).toString(),
          skill: skill.id ? { value: skill.id.toString(), label: skill.name } : null,
          years: skill.yearsOfExperience?.toString() || ''
        })));
      }

      // Map secondary skills
      if (data.secondarySkills && data.secondarySkills.length > 0) {
        setSecondarySkills(data.secondarySkills.map((skill, index) => ({
          id: (index + 1).toString(),
          skill: skill.id ? { value: skill.id.toString(), label: skill.name } : null,
          years: skill.yearsOfExperience?.toString() || ''
        })));
      }

      // Map primary technologies
      if (data.primaryTechnologies && data.primaryTechnologies.length > 0) {
        setPrimaryTechnologies(data.primaryTechnologies.map((tech, index) => ({
          id: (index + 1).toString(),
          technology: tech.id ? { value: tech.id.toString(), label: tech.name } : null,
          years: tech.yearsOfExperience?.toString() || ''
        })));
      }

      // Map secondary technologies
      if (data.secondaryTechnologies && data.secondaryTechnologies.length > 0) {
        setSecondaryTechnologies(data.secondaryTechnologies.map((tech, index) => ({
          id: (index + 1).toString(),
          technology: tech.id ? { value: tech.id.toString(), label: tech.name } : null,
          years: tech.yearsOfExperience?.toString() || ''
        })));
      }
      setIsLoading(false);
    });
  }, [type, params.id, addedVacancyId, pageId, setPageId]);

  const handleSave = () => {
    const newErrors =
      validateVacancyForm(
        basicData,
        customers,
        qualifications,
        primarySkills,
        primaryTechnologies,
      ) ?? {};

    setErrors(newErrors);
    if (Object.keys(newErrors).length >= 1) {
      onError('Please fix the errors before saving.');
      return;
    }

    if (type === 'add') {
      handleAddVacancySave();
    } else {
      handleEditVacancySave();
    }
  };

  const buildFormData = (): FormData =>
    buildEditVacancyFormData(
      basicData,
      customers,
      qualifications,
      primarySkills,
      secondarySkills,
      primaryTechnologies,
      secondaryTechnologies,
      initialIsActive,
      initialIsVisible,
    );

  const handleEditVacancySave = async () => {
    try {
      setIsSaving(true);
      const vacancyId = params?.id ? params.id : addedVacancyId;
      if (!vacancyId) return;

      const formData = buildFormData();
      // First call without forceUnpublish flag

      const response = await api.put<VacancyApiResponse & { requiresConfirmation?: boolean }>(
        VACANCY_ENDPOINTS.updateDetails(vacancyId),
        formData,
        {
          headers: {
            ...API_HEADERS.VACANCY_EDIT_FORM_UPDATE_DETAILS,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.status === 200) {
        // Check if confirmation is required
        if (response.data.requiresConfirmation) {
          setIsConfirmModalOpen(true);
          setIsSaving(false);
        } else {
          onSuccess('Vacancy updated successfully!');
          setIsSaving(false);
        }
      }
    } catch (error: any) {
      console.error('Error updating vacancy:', error);

      if (error.response?.status === 400) {
        const data = error.response?.data;
        const apiErrors = data?.errors ?? (typeof data === 'object' && !Array.isArray(data) ? data : null);
        if (apiErrors && typeof apiErrors === 'object') {
          setErrors(prev => ({ ...prev, ...apiErrors }));
        }
        const msg = data?.message || (apiErrors && Object.values(apiErrors)[0] as string) || 'Please fill all required fields';
        onError(msg);
        setIsSaving(false);
        return;
      }

      onError('Failed to update vacancy. Please try again.');
      setIsSaving(false);
    }
  };

  const handleConfirmUpdate = async () => {
    try {
      setIsConfirming(true);
      const vacancyId = params?.id ? params.id : addedVacancyId;
      if (!vacancyId) return;

      // Rebuild formData with forceUnpublish flag
      const formData = buildFormData();
      formData.append('forceUnpublish', 'true');

      const response = await api.put<VacancyApiResponse>(
        VACANCY_ENDPOINTS.updateDetails(vacancyId),
        formData,
        {
          headers: {
            ...API_HEADERS.VACANCY_EDIT_FORM_UPDATE_DETAILS,
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      if (response.status === 200) {
        // Update local state to reflect unpublished and approval pending
        setBasicData(prev => ({
          ...prev,
          approvalStatus: 0,
          jobPostPublished: 0,           // Reset published flag
          jobPostPublishedStatus: false  // Unlocks the Vacancy Code field
        }));

        setInitialIsActive(basicData.isActive);
        setInitialIsVisible(basicData.isVisible);
        
        // Always call deleteJob API to remove from website (same as world icon unpublish)
        try {
          await api.post(VACANCY_ENDPOINTS.deleteJob(vacancyId), {}, {
            headers: { ...API_HEADERS.VACANCY_SEARCH_DELETE_JOB },
          });
        } catch (deleteError) {
          console.error('Error unpublishing from website:', deleteError);
          // Continue anyway - the internal unpublish worked
        }
        
        setIsConfirmModalOpen(false);
        onSuccess('Vacancy updated successfully! The vacancy has been unpublished and approval status is now pending.');
      }
    } catch (error: any) {
      console.error('Error confirming update:', error);

      if (error.response?.status === 400) {
        const data = error.response?.data;
        const apiErrors = data?.errors ?? (typeof data === 'object' && !Array.isArray(data) ? data : null);
        if (apiErrors && typeof apiErrors === 'object') {
          setErrors(prev => ({ ...prev, ...apiErrors }));
        }
        const msg = data?.message || (apiErrors && Object.values(apiErrors)[0] as string) || 'Please fill all required fields';
        onError(msg);
        setIsConfirming(false);
        return;
      }

      onError('Failed to confirm update. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddVacancySave = async () => {
    try {
      setIsSaving(true);

      const formData = buildAddVacancyPayload(
        basicData,
        customers,
        qualifications,
        primarySkills,
        secondarySkills,
        primaryTechnologies,
        secondaryTechnologies,
      );

      const response = await api.post<VacancyApiResponse>(
        VACANCY_ENDPOINTS.add,
        formData,
        {
          headers: { ...API_HEADERS.VACANCY_ADD_CREATE },
        },
      );

      if (response.status === 201) {
        onSuccess('Vacancy created successfully!');
        if (setAddedVacancyId) {
          setAddedVacancyId(response.data.id);
        }
      }
    } catch (error: any) {
      console.error('Error saving vacancy:', error);

      if (error.response?.status === 400) {
        const data = error.response?.data;
        const apiErrors = data?.errors ?? (typeof data === 'object' && !Array.isArray(data) ? data : null);
        if (apiErrors && typeof apiErrors === 'object') {
          setErrors(prev => ({ ...prev, ...apiErrors }));
        }
        const msg = data?.message || (apiErrors && Object.values(apiErrors)[0] as string) || 'Please fill all required fields';
        onError(msg);
        setIsSaving(false);
        return;
      }

      onError('Failed to create vacancy. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddCustomer = () => {
    setCustomers([...customers, { rowId: crypto.randomUUID(), id: '', name: '', countryCode: '', numberOfVacancies: '', revenuePerResource: '' }]);
  };

  const handleRemoveCustomer = (rowId: string) => {
    setCustomers(customers.filter(customer => customer.rowId !== rowId));
  };

  return (
    <Box gap={3} display="flex" flexDirection="column">
      {isLoading ? (
        <Loading />
      ) : (
        <>
          {/* Basic Information */}
          <VacancyBasicCard
            type={type}
            errors={errors}
            setErrors={setErrors}
            basicData={basicData}
            setBasicData={setBasicData}
          />

          {/* Customer Information */}
          <VacancyCustomerCard
            type={type}
            errors={errors}
            setErrors={setErrors}
            customers={customers}
            onAddCustomer={handleAddCustomer}
            onRemoveCustomer={handleRemoveCustomer}
            setCustomers={setCustomers}
          />

          {/* Skills and Technologies */}
          <VacancySkillCard
            type={type}
            errors={errors}
            setErrors={setErrors}
            qualifications={qualifications}
            primarySkills={primarySkills}
            secondarySkills={secondarySkills}
            primaryTechnologies={primaryTechnologies}
            secondaryTechnologies={secondaryTechnologies}
            setQualifications={setQualifications}
            setPrimarySkills={setPrimarySkills}
            setSecondarySkills={setSecondarySkills}
            setPrimaryTechnologies={setPrimaryTechnologies}
            setSecondaryTechnologies={setSecondaryTechnologies}
            onError={onError}
          />

          {/* Action Buttons */}
          {((type === 'add' && hasPermission(PERMISSION_IDS.VACANCY_FORM_SAVE_ADD)) ||
            (type === 'edit' && hasPermission(PERMISSION_IDS.VACANCY_FORM_SAVE_EDIT))) && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                width: '1562px',
              }}
            >
              <Button
                variant="outlined"
                onClick={onCancel}
                className="edit-section-cancel-button"
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                isLoading={isSaving}
                variant="contained"
                onClick={handleSave}
                className="edit-section-save-button"
                disabled={isSaving || isConfirming || (type === 'add' && (addedVacancyId !== undefined && addedVacancyId !== null))}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          )}
        </>
      )}
      
      {/* Confirmation Modal */}
      <ConfirmationModal
        open={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
        }}
        onConfirm={handleConfirmUpdate}
        title="Unpublish Vacancy?"
        message="If you want to do these changes, the vacancy will be unpublished. Do you want to continue?"
        confirmText="Yes"
        cancelText="No"
        isActioning={isConfirming}
      />
    </Box>
  );
};

export default VacancyForm;
